import { useEffect, useRef, useState } from 'react';
import { ElementCard } from '@/components/content/ElementCard';

const sources = [
  'Hightouch',
  'Census',
  'RudderStack',
  'Segment',
  'Snowflake',
  'Databricks',
];
const surfaces = ['Contentful', 'Web', 'Mobile', 'Video', 'Modules', 'Email'];

export function OrbitDiagram() {
  const size = 560;
  const cx = size / 2;
  const cy = size / 2;
  const innerR = 130;
  const outerR = 230;

  const [srcAngle, setSrcAngle] = useState(0);
  const [surfAngle, setSurfAngle] = useState(0);
  const rafRef = useRef<number>(0);
  const lastRef = useRef<number | null>(null);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const animate = (ts: number) => {
      if (lastRef.current != null) {
        const dt = ts - lastRef.current;
        setSrcAngle((a) => (a + dt * 0.00022) % (Math.PI * 2));
        setSurfAngle((a) => (a - dt * 0.00015) % (Math.PI * 2));
      }
      lastRef.current = ts;
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: size,
        margin: '0 auto',
      }}
    >
      <svg
        aria-hidden="true"
        viewBox={`0 0 ${size} ${size}`}
        style={{ width: '100%', height: 'auto' }}
      >
        <defs>
          <radialGradient id="orbit-glow" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stopColor="var(--accent)" stopOpacity="0.25" />
            <stop offset="0.6" stopColor="var(--accent)" stopOpacity="0.05" />
            <stop offset="1" stopColor="var(--accent)" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx={cx} cy={cy} r={outerR + 30} fill="url(#orbit-glow)" />
        <ellipse
          cx={cx}
          cy={cy}
          rx={outerR}
          ry={outerR * 0.55}
          stroke="var(--border-default)"
          strokeDasharray="2 4"
          fill="none"
        />
        <ellipse
          cx={cx}
          cy={cy}
          rx={innerR}
          ry={innerR * 0.55}
          stroke="var(--border-default)"
          strokeDasharray="2 4"
          fill="none"
        />

        {sources.map((s, i) => {
          const t = (i / sources.length) * Math.PI * 2 + srcAngle;
          const x = cx + Math.cos(t) * innerR;
          const y = cy + Math.sin(t) * innerR * 0.55;
          const labelAbove = Math.sin(t) <= 0;
          return (
            <g key={s}>
              <circle
                cx={x}
                cy={y}
                r="5"
                fill="var(--accent)"
                opacity={0.85 + 0.15 * Math.sin(t + Math.PI / 2)}
              />
              <text
                x={x}
                y={labelAbove ? y - 10 : y + 20}
                textAnchor="middle"
                fontSize="11"
                fontFamily="var(--font-mono)"
                fill="var(--ink-secondary)"
              >
                {s}
              </text>
            </g>
          );
        })}

        {surfaces.map((s, i) => {
          const t = (i / surfaces.length) * Math.PI * 2 + surfAngle;
          const x = cx + Math.cos(t) * outerR;
          const y = cy + Math.sin(t) * outerR * 0.55;
          const labelAbove = Math.sin(t) <= 0;
          return (
            <g key={s}>
              <circle
                cx={x}
                cy={y}
                r="4"
                fill="var(--gray-300)"
                opacity={0.7 + 0.3 * Math.cos(t)}
              />
              <text
                x={x}
                y={labelAbove ? y - 10 : y + 18}
                textAnchor="middle"
                fontSize="11"
                fontFamily="var(--font-mono)"
                fill="var(--ink-muted)"
              >
                {s}
              </text>
            </g>
          );
        })}
      </svg>

      <div
        className="glass-chip"
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          padding: '12px 16px',
          border: '1px solid var(--accent)',
          borderRadius: 10,
          boxShadow:
            '0 0 36px var(--radium-glow), inset 0 1px 0 rgba(255,255,255,0.12)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <ElementCard symbol="Ra" number="88" label="Radium" size="sm" />
      </div>

      <div
        style={{ position: 'absolute', left: 0, top: '8%' }}
        className="text-eyebrow"
      >
        Surfaces
      </div>
      <div
        style={{
          position: 'absolute',
          right: 0,
          top: '8%',
          textAlign: 'right',
        }}
        className="text-eyebrow"
      >
        Sources
      </div>
    </div>
  );
}
